import { useCallback, useEffect, useRef } from 'react'
import type { AuditAction } from '@behuman/core-contract'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'
import { AUDIT_REPORT_DEBOUNCE_MS } from './useAuditSectionReporting'

/**
 * PD-1322 (S14) — document open/download/export reporting, same discipline as
 * section views (useAuditSectionReporting): a debounce so rapid document-flipping
 * never reports the skimmed ones, at most one report per document per PATIENT CHART
 * VISIT, reset when the patient changes.
 *
 * The reported sets live at module level (not in the hook) because Documents
 * can unmount/remount while the user stays on the same patient chart — a
 * per-mount ref would re-report on every remount, breaking the
 * once-per-visit claim.
 *
 * The client only reports a coarse action code — the server authors the audit
 * record (activity, sections, download flag, actor, timestamp). Since QA round 1
 * (M2) a report may also NAME its DocumentReference: the server reads the resource
 * through the caller's own client and derives the real section and a titled
 * activity from it, so nothing the client says is trusted.
 */
let reportedPatientId: string | undefined
const reportedOpens = new Set<string>()
const reportedDownloads = new Set<string>()
const reportedExports = new Set<string>()

/**
 * When each not-yet-reported record's debounce started, by key. Module level for the same
 * reason the reported sets are, and for a sharper one.
 *
 * QA round 3 (re-test) — AN ORDER WHOSE ATTACHMENT IS A TIFF RECORDED NOTHING AT ALL. The
 * debounce used to live entirely inside the effect: a `setTimeout` created on every run and
 * cleared by the cleanup. That makes the countdown a property of the EFFECT INSTANCE rather
 * than of the record on screen, so anything that re-runs the effect or remounts the
 * component restarts it from zero — and a viewer whose surroundings churn faster than the
 * debounce never reports at all. The Orders tab does exactly that kind of churn for a TIFF:
 * the conversion is an extra async pipeline writing component state, on the one attachment
 * type that also blocks the main thread while it decodes.
 *
 * Keyed by RECORD, the clock cannot be restarted by rendering. It measures the time the
 * record has been on screen during this chart visit — cumulative rather than uninterrupted,
 * which is the direction an append-only compliance log must err in: it can report a record
 * the reader lingered over in two sittings, and it can never silently drop one.
 */
const pendingSince = new Map<string, number>()

function reportedSetsFor(patientId: string): {
  opens: Set<string>
  downloads: Set<string>
  exports: Set<string>
} {
  if (patientId !== reportedPatientId) {
    reportedPatientId = patientId
    reportedOpens.clear()
    reportedDownloads.clear()
    reportedExports.clear()
    pendingSince.clear()
  }
  return { opens: reportedOpens, downloads: reportedDownloads, exports: reportedExports }
}

/** Test-only: clear the module-level reported sets between test cases. */
export function resetAuditDocumentReportingForTests(): void {
  reportedPatientId = undefined
  reportedOpens.clear()
  reportedDownloads.clear()
  reportedExports.clear()
  pendingSince.clear()
}

/**
 * One openable record, as the reporting sees it. `key` dedupes within the chart visit.
 * `documentReferenceId` rides in the payload when the opened thing is a
 * DocumentReference (M2); `record` (QA round 3) generalizes it to the other openable
 * records — an order group or a lab report, which open with no DocumentReference
 * existing. Either way the server reads the named resource itself and derives the
 * event's title and section from it, never from us.
 */
export type AuditOpenEntry = {
  key: string
  documentReferenceId?: string
  record?: { resourceType: 'RequestGroup' | 'DiagnosticReport'; id: string }
}

/**
 * QA round 1 (W7) — the shared open-reporting core, so every tab that displays
 * documents (Documents, Orders, Labs/Imaging) reports through ONE debounced,
 * once-per-visit path instead of growing per-section variants.
 */
function useAuditOpenReporting(patientId: string | undefined, entries: AuditOpenEntry[]): void {
  const client = useClient()
  // The entries array is rebuilt every render; the effect keys on the stable
  // serialized keys and reads the current entries through a ref.
  const entriesRef = useRef(entries)
  entriesRef.current = entries
  const serializedKeys = entries.map((entry) => entry.key).join('|')

  useEffect(() => {
    if (!patientId || serializedKeys === '') return
    const { opens } = reportedSetsFor(patientId)
    let timer: ReturnType<typeof setTimeout> | undefined

    /** Milliseconds left on this record's debounce, starting its clock the first time. */
    const remainingFor = (key: string): number => {
      const startedAt = pendingSince.get(key)
      if (startedAt === undefined) {
        pendingSince.set(key, Date.now())
        return AUDIT_REPORT_DEBOUNCE_MS
      }
      return Math.max(0, startedAt + AUDIT_REPORT_DEBOUNCE_MS - Date.now())
    }

    const report = (entry: AuditOpenEntry): void => {
      // Mark before the request so a slow response can't double-report;
      // unmark on failure so a retry is possible on the next open.
      opens.add(entry.key)
      pendingSince.delete(entry.key)
      // Each entry fully insulated: a client that rejects SYNCHRONOUSLY (an input the
      // contract refuses) would otherwise throw out of the loop below and take every
      // record after it with it — silently, since a throw inside a timer callback reaches
      // nobody. On an append-only log a skipped record is indistinguishable from one that
      // never happened.
      try {
        client
          .auditReportAction({
            patientId,
            // A record open is its own action code: "Opened record: Lipid Panel" for an
            // order or a lab, "Opened document: …" for a real document.
            action: entry.record !== undefined ? 'record-open' : 'document-open',
            ...(entry.documentReferenceId !== undefined
              ? { documentReferenceId: entry.documentReferenceId }
              : {}),
            ...(entry.record !== undefined ? { record: entry.record } : {}),
          })
          .catch((err) => {
            opens.delete(entry.key)
            logError('patient', 'Failed to report document open', err)
          })
      } catch (err) {
        opens.delete(entry.key)
        logError('patient', 'Failed to report document open', err)
      }
    }

    // Reports whatever has settled and re-arms for whatever has not. Self-rescheduling
    // rather than one timer per effect run: records on screen together can be due at
    // different moments (the list grew), and a single timer sized to the soonest of them
    // would leave the rest permanently unreported.
    const settleDue = (): void => {
      let nextWait = Number.POSITIVE_INFINITY
      for (const entry of entriesRef.current) {
        if (opens.has(entry.key)) continue
        const remaining = remainingFor(entry.key)
        if (remaining > 0) {
          nextWait = Math.min(nextWait, remaining)
          continue
        }
        report(entry)
      }
      if (nextWait !== Number.POSITIVE_INFINITY) timer = setTimeout(settleDue, nextWait)
    }

    settleDue()
    return () => {
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [client, patientId, serializedKeys])
}

/** One DocumentReference on screen (the Documents tab's viewer). */
export function useAuditDocumentOpenReporting(
  patientId: string | undefined,
  documentReferenceId: string | undefined,
): void {
  useAuditOpenReporting(
    patientId,
    documentReferenceId !== undefined ? [{ key: documentReferenceId, documentReferenceId }] : [],
  )
}

/**
 * QA round 1 (W7) — several DocumentReferences on screen at once (the Orders tab
 * renders every attachment of the order inline).
 */
export function useAuditDocumentsOpenReporting(
  patientId: string | undefined,
  documentReferenceIds: string[],
): void {
  useAuditOpenReporting(
    patientId,
    documentReferenceIds.map((id) => ({ key: id, documentReferenceId: id })),
  )
}

/**
 * QA round 3 — an opened chart RECORD that is not a document: an order group or a lab
 * report. Replaces round 1's attachment-keyed reporting, which keyed the event on a
 * renderable attachment existing — so an order without a PDF logged nothing, and a lab
 * open carried neither the lab's name nor its section. The record open reports
 * unconditionally; the server reads the record and names/sections the event from it.
 */
export function useAuditRecordOpenReporting(
  patientId: string | undefined,
  record: { resourceType: 'RequestGroup' | 'DiagnosticReport'; id: string } | undefined,
): void {
  useAuditOpenReporting(
    patientId,
    record !== undefined ? [{ key: `${record.resourceType}/${record.id}`, record }] : [],
  )
}

/**
 * Returns a `report(documentReferenceId)` callback for a download or export choke
 * point. Reports at most once per document per patient chart visit — repeated clicks
 * on the same control don't spam the store. Download and Export are SEPARATE actions
 * (QA M3): the record must say what the user-facing control said.
 */
function useAuditDocumentActionReporting(
  patientId: string | undefined,
  action: Extract<AuditAction, 'document-download' | 'document-export'>,
): (documentReferenceId: string) => void {
  const client = useClient()

  return useCallback(
    (documentReferenceId: string) => {
      if (!patientId) return
      const sets = reportedSetsFor(patientId)
      const reported = action === 'document-export' ? sets.exports : sets.downloads
      if (reported.has(documentReferenceId)) return
      reported.add(documentReferenceId)
      client.auditReportAction({ patientId, action, documentReferenceId }).catch((err) => {
        reported.delete(documentReferenceId)
        logError('patient', `Failed to report ${action}`, err)
      })
    },
    [client, patientId, action],
  )
}

/** The Download controls (attachment links). */
export function useAuditDownloadReporting(
  patientId: string | undefined,
): (documentReferenceId: string) => void {
  return useAuditDocumentActionReporting(patientId, 'document-download')
}

/** QA round 1 (M3) — the controls that SAY Export (the progress-note PDF export). */
export function useAuditExportReporting(
  patientId: string | undefined,
): (documentReferenceId: string) => void {
  return useAuditDocumentActionReporting(patientId, 'document-export')
}
